import { FormHelper } from '@presentation/components/FormHelper';
import { ToastService } from '@presentation/components/Toast';
import {
  messageRevenuSousRmm,
  messageSalaireSousSmig,
  revenuSousRmm,
  salaireSousSmig,
} from '@shared/validation/SalarieSchema';
import { SalarieSchema } from '@shared/validation/SalarieSchema';
import { AppContext } from '@application/context/AppContext';
import {
  linkSalarieToMatchingUser,
  normalizeEmail,
} from '@application/auth/UserSalarieLinkService';
import { matriculeDejaPris } from '@domain/paie/SalarieUniqueness';
import { prochainMatricule } from '@domain/rh/ProchainMatricule';
import { uuid } from '@shared/utils/uuid';
import type { Salarie } from '@shared/types/Salarie';
import {
  buildSalarieFormTabsHtml,
  parseSalarieFormExtras,
  wireSalarieFormDynamics,
} from './salarie/salarieFormTabs';
import {
  applyFormFieldValues,
  clearSalarieFormDraft,
  collectFormFieldValues,
  loadSalarieFormDraft,
  saveSalarieFormDraft,
} from './salarie/SalarieFormDraft';

export class SalarieFormView {
  static render(
    container: HTMLElement,
    onSaved: () => void | Promise<void>,
    onCancel = onSaved,
    existing?: Salarie,
    salariesForMatricule?: ReadonlyArray<{ matricule: string }>,
  ): void {
    const edit = Boolean(existing);
    const nextMatricule =
      !edit && salariesForMatricule ? prochainMatricule(salariesForMatricule) : undefined;
    const draftId: string | 'new' = existing?.id ?? 'new';

    container.innerHTML = `
      <form id="salarie-form" class="salarie-form" novalidate>
        ${buildSalarieFormTabsHtml(existing, nextMatricule)}
        <div class="salarie-form-footer">
          <button type="button" id="cancel" class="btn btn-secondary">Annuler</button>
          <button type="submit" class="btn btn-primary">Enregistrer</button>
        </div>
      </form>
    `;

    const form = container.querySelector<HTMLFormElement>('#salarie-form')!;
    wireSalarieFormDynamics(container, form, existing);

    const tenantId = (): string => {
      try {
        return AppContext.getInstance().tenantContext.tenantId();
      } catch {
        return 'default';
      }
    };

    // Après un enregistrement réussi, plus aucun brouillon ne doit être écrit (minuteur de 600 ms encore actif).
    let enregistre = false;
    const persistDraft = (): void => {
      if (enregistre) return;
      const activeTab = form
        .querySelector<HTMLButtonElement>('.salarie-form-tabs .tab.active')
        ?.dataset.tab;
      saveSalarieFormDraft(tenantId(), draftId, collectFormFieldValues(form), activeTab);
    };

    // Reprise brouillon (coupure / plantage)
    const draft = loadSalarieFormDraft(tenantId(), draftId);
    if (draft && Object.keys(draft.fields).length > 0) {
      applyFormFieldValues(form, draft.fields);
      form.querySelector<HTMLSelectElement>('[name="type_piece"]')?.dispatchEvent(
        new Event('change', { bubbles: true }),
      );
      form.querySelector<HTMLSelectElement>('[name="situation"]')?.dispatchEvent(
        new Event('change', { bubbles: true }),
      );
      if (draft.activeTab) {
        form.querySelectorAll<HTMLButtonElement>('.salarie-form-tabs .tab').forEach((t) => {
          t.classList.toggle('active', t.dataset.tab === draft.activeTab);
        });
        form.querySelectorAll<HTMLElement>('.tab-content').forEach((p) => {
          p.classList.toggle('active', p.id === draft.activeTab);
        });
      }
      const when = draft.savedAt
        ? new Date(draft.savedAt).toLocaleString('fr-FR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })
        : '';
      ToastService.info(
        when
          ? `Brouillon repris (${when}). Vérifiez puis enregistrez.`
          : 'Brouillon repris. Vérifiez puis enregistrez.',
      );
    }

    let draftTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleDraft = (): void => {
      if (draftTimer) clearTimeout(draftTimer);
      draftTimer = setTimeout(persistDraft, 600);
    };
    form.addEventListener('input', scheduleDraft);
    form.addEventListener('change', scheduleDraft);
    window.addEventListener('beforeunload', persistDraft);

    container.querySelector('#cancel')?.addEventListener('click', () => {
      window.removeEventListener('beforeunload', persistDraft);
      onCancel();
    });

    if (!edit) {
      const matriculeInput = form.querySelector<HTMLInputElement>('[name="matricule"]');
      const unlockMatricule = (list: ReadonlyArray<{ matricule: string }>): void => {
        if (!matriculeInput) return;
        if (!matriculeInput.value.trim()) {
          const next = prochainMatricule(list);
          matriculeInput.value = next;
          matriculeInput.placeholder = next;
        }
        matriculeInput.readOnly = false;
        matriculeInput.removeAttribute('readonly');
      };
      if (salariesForMatricule) {
        unlockMatricule(salariesForMatricule);
      } else {
        void AppContext.getInstance()
          .salaries.list()
          .then((tous) => unlockMatricule(tous))
          .catch(() => undefined);
      }
    }

    FormHelper.bindSubmit(form, SalarieSchema, async (data) => {
      const ctx = AppContext.getInstance();
      const tous = await ctx.salaries.list();
      const extras = parseSalarieFormExtras(form, container);

      let matricule = data.matricule.trim();
      if (!edit && !matricule) {
        matricule = prochainMatricule(tous);
      }

      if (matriculeDejaPris(tous, matricule, existing?.id)) {
        FormHelper.markFieldError(form, 'matricule', 'Matricule déjà attribué');
        FormHelper.focusFirstInvalid(form);
        ToastService.error('Matricule déjà attribué');
        return;
      }

      const email = normalizeEmail(data.email);
      const enfants = Number(data.enfants ?? 0) || 0;
      const statutContrat = data.statut_contrat === 'essai' ? 'CDI' : data.statut_contrat;

      const familyPatch = {
        enfants,
        nb_enfants: enfants,
        conjoint_actif: extras.conjoint_salarie,
        conjoint_salarie: extras.conjoint_salarie,
        conjoint_cnamgs: extras.conjoint_cnamgs,
        situation_familiale: data.situation,
        // La date du décès n'a de sens que pour un(e) veuf(ve) : effacée sinon (jamais de date périmée en base).
        date_deces_conjoint: data.situation === 'veuf' ? data.date_deces_conjoint : undefined,
        enfants_detail: extras.enfants_detail,
        vaccins_archive: extras.vaccins_archive,
        medecine_ok: extras.medecine_ok,
        quota_etranger: extras.quota_etranger,
        cnss: extras.cnss,
        numero_cnss: data.numero_cnss,
        statut_contrat: statutContrat,
      };

      if (existing) {
        const salarie: Salarie = {
          ...existing,
          ...data,
          ...familyPatch,
          matricule,
          email,
        };
        await ctx.salaries.update(salarie);
        await linkSalarieToMatchingUser(ctx, salarie);
        ToastService.success(`Salarié enregistré — ${data.prenom} ${data.nom}`);
        if (salaireSousSmig(data.salaire_base)) {
          ToastService.warning(messageSalaireSousSmig());
        } else if (revenuSousRmm(data)) {
          ToastService.warning(messageRevenuSousRmm());
        }
      } else {
        const salarie: Salarie = {
          ...data,
          ...familyPatch,
          matricule,
          email,
          id: uuid('sal'),
          tenant_id: ctx.tenantContext.tenantId(),
          cree_le: new Date().toISOString(),
        } as Salarie;
        await ctx.salaries.add(salarie);
        const compte = await linkSalarieToMatchingUser(ctx, salarie);
        ToastService.success(
          compte
            ? `Salarié enregistré — ${data.prenom} ${data.nom} (${matricule}) — compte ${compte.email}`
            : `Salarié enregistré — ${data.prenom} ${data.nom} (${matricule})`,
        );
        if (salaireSousSmig(data.salaire_base)) {
          ToastService.warning(messageSalaireSousSmig());
        } else if (revenuSousRmm(data)) {
          ToastService.warning(messageRevenuSousRmm());
        }
        void import('../../shell/OnboardingChecklist').then(({ markOnboardingStep }) => {
          markOnboardingStep('salarie');
        });
      }
      enregistre = true;
      if (draftTimer) clearTimeout(draftTimer);
      window.removeEventListener('beforeunload', persistDraft);
      clearSalarieFormDraft(tenantId(), draftId);
      await onSaved();
    });
  }
}
